/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    Component, 
    ElementRef, 
    EventEmitter, 
    Input, 
    OnInit, 
    OnDestroy, 
    Output, 
    Renderer2, 
    ViewChild,
    TemplateRef
} from '@angular/core';
import * as _ from 'lodash';
import {Subject} from 'rxjs';
import {FcAgDatepickerTypes} from './model/types';
import {FcDatepickerData, FcDatePickerFormat} from './model/fc-datepicker-data.model';

/**
 * Date picker component with Angular.
 */
@Component({
    selector: 'fc-ag-date-picker',
    templateUrl: './fc-ag-date-picker.component.html',
    styleUrls: ['./fc-ag-date-picker.component.scss']
})
export class FcAgDatePickerComponent implements OnInit, OnDestroy {

    /**
     * Format date lead to performance issue if using in html with return value from function.
     * Need to cache value for next time.
     */
    dateCache: any = {};
    
    /**
     * FC Datepicker type.
     */
    readonly DP_TYPES: any = FcAgDatepickerTypes;

    /**
     * Default placeholder.
     */
    readonly PLACEHOLDER: string = '--/---/----';

    /**
     * Flag to show or hide calendar.
     */
    showCalendar: boolean = false;

    /**
     * Single datepicker model.
     */
    @Input() set singleDatepickerModel(startDate: Date|null|undefined) {
        this.setFromDate(startDate);
    }

    /**
     * Range 'From' datepicker model.
     */
    @Input() set rangeFromDatepickerModel(startDate: Date|null|undefined) {
        this.setFromDate(startDate);
    }

    /**
     * Range 'To' datepicker model.
     */
    @Input() set rangeToDatepickerModel(endDate: Date|null|undefined) {
        this.setToDate(endDate);
    }

    /**
     * To date will get end of 23 hours 59 minutes 59 seconds.
     *
     * @param year Year value.
     * @param month Month value.
     * @param day Day value.
     * @returns Date.
     */
    rangeEndDateGenerator: Function = function(year: number, month: number, day: number): Date {
        return new Date(year, month, day, 23, 59, 59, 999);
    }
    
    /**
     * ElementRef of datepickerWrapper.
     */
    @ViewChild('datepickerWrapper') dpWrapperElement: ElementRef;

    /**
     * ElementRef of datepickerPanelWrapper.
     */
    @ViewChild('datepickerPanelWrapper') dpPanelWrapperElement: ElementRef;

    /**
     * ElementRef of datepickerInput.
     */
    @ViewChild('datepickerInput') dpInputElement: ElementRef;

    /**
     * ElementRef of datepickerIcon.
     */
    @ViewChild('datepickerIcon') dpIconElement: ElementRef;

    private _datepickerData: FcDatepickerData;

    /**
     * Getter datepicker data.
     *
     * @returns FcDatepickerData.
     */
    get datepickerData(): FcDatepickerData {
        if (_.isNil(this._datepickerData)) {
            this._datepickerData = {
                filter: undefined,
                startDate: undefined,
                endDate: undefined
            };
        }
        return this._datepickerData;
    }

    /**
     * Setter datepicker data.
     *
     * @param _dd FcDatepickerData.
     */
    set datepickerData(_dd: FcDatepickerData) {
        if (_.isNil(_dd)) {
            this._datepickerData = {
                filter: undefined,
                startDate: undefined,
                endDate: undefined
            };
        } else {
            this._datepickerData = _dd;
        }
    }

    /**
     * To set 'from' date directly without open calendar.
     *
     * @param _df Date.
     */
    setFromDate(_df: Date|null|undefined) {
        if (!_.isNil(_df) && !_.isNil(this._datepickerData)) {
            this.datepickerData.startDate = _df;
            this.emittingData();
        }
    }

    /**
     * To set 'to' date directly without open calendar.
     *
     * @param _dt Date.
     */
    setToDate(_dt: Date|null|undefined) {
        if (!_.isNil(_dt) && !_.isNil(this._datepickerData)) {
            this.datepickerData.endDate = _dt;
            this.emittingData();
        }
    }

    private _filterItems: any = [];

    /**
     * Getter filter items.
     *
     * @returns Any.
     */
    get filterItems(): any {
        return this._filterItems;
    }

    /**
     * Input/Setter filter items.
     *
     * @param value Any.
     */
    @Input() set filterItems(value: any) {
        if (_.isNil(value)) {
            value = [];
        }
        this._filterItems = value;
    }

    /**
     * Date Picker Clear Data Subject.
     */
    @Input() datePickerClearDataSubject?: Subject<any> | null;

    private _selectedFilterItem: any;

    /**
     * Getter selected filter item.
     *
     * @returns Any.
     */
    get selectedFilterItem(): any {
        return this._selectedFilterItem;
    }

    /**
     * Input/Setter selected filter item.
     *
     * @param value Any.
     */
    @Input() set selectedFilterItem(value: any) {
        if (_.isNil(value)) {
            value = [];
        }
        this._datepickerData.filter = value[0];
        this._selectedFilterItem = value;
    }

    private _filterBindingKey: string = 'key';

    /**
     * Getter filter binding key.
     *
     * @returns String.
     */
    get filterBindingKey(): string {
        return this._filterBindingKey;
    }

    /**
     * Setter filter binding key.
     *
     * @param value String.
     */
    set filterBindingKey(value: string) {
        if (_.isNil(value)) {
            value = 'key';
        }
        this._filterBindingKey = value;
    }

    private _filterBindingValue: string = 'value';

    /**
     * Getter filter binding value.
     *
     * @returns String.
     */
    get filterBindingValue(): string {
        return this._filterBindingValue;
    }

    /**
     * Setter filter binding value.
     *
     * @param value String.
     */
    set filterBindingValue(value: string) {
        if (_.isNil(value)) {
            value = 'value';
        }
        this._filterBindingValue = value;
    }

    private _datepickerType: number = FcAgDatepickerTypes.SINGLE_DP;

    /**
     * Getter datepicker type.
     *
     * @returns Number.
     */
    get datepickerType(): number {
        return this._datepickerType;
    }

    /**
     * Input/Setter datepicker type.
     *
     * @param _dt Number.
     */
    @Input()
    set datepickerType(_dt: number) {
        if (_.isNil(_dt)) {
            this._datepickerType = FcAgDatepickerTypes.SINGLE_DP;
            return;
        }
        this._datepickerType = _dt;
    }

    private _inputPlaceholder: string = '';

    /**
     * Getter input placeholder.
     *
     * @returns String.
     */
    get inputPlaceholder(): string {
        return this._inputPlaceholder;
    }

    /**
     * Setter input placeholder.
     *
     * @param _ph String.
     */
    @Input()
    set inputPlaceholder(_ph: string) {
        if (_ph) {
            this._inputPlaceholder = _ph;
        } else {
            this._inputPlaceholder = 'Select Date';
        }
    }

    /**
     * The maximum valid date.
     */
    @Input() maxDate!: Date;

    /**
     * The minimum valid date.
     */
    @Input() minDate!: Date;
    
    /**
     * Whether to show clear button.
     */
    @Input() allowClear: boolean = true

    /**
     * Render extra footer in panel.
     */
    @Input() extraFooter!: TemplateRef<any>|null;
    
    /**
     * Date format.
     */
    @Input() dateFormat: FcDatePickerFormat;

    /**
     * Close panel after selection.
     */
    @Input() closePanelAfterSelection: boolean  = false;

    /**
     * Allow select month and year.
     */
    @Input() allowSelectMonthYear: boolean = false;

    /**
     * Output/Emitter filter change.
     */
    @Output() filterChange: EventEmitter<any>;

    /**
     * Output/Emitter datepicker model change.
     */
    @Output() datepickerModelChange: EventEmitter<any>;

    /**
     * Input of dropdown label (top label, if not set, will disappear).
     *
     * @returns String.
     */
    @Input() dropdownLabel: string = '';

    /**
     * FcAgDatePicker constructor.
     *
     * @param renderer Renderer2.
     */
    constructor(private renderer: Renderer2) {
        this.filterChange = new EventEmitter<any>();
        this.datepickerModelChange = new EventEmitter<any>();
        this.datepickerData = {
            filter: undefined,
            startDate: undefined,
            endDate: undefined
        };
    }

    /**
     * Angular OnInit implementation.
     */
    ngOnInit(): void {
        this.renderer.listen('window', 'click', (e: any) => {
            if (!this.dpInputElement.nativeElement.contains(e.target)
                && !this.dpIconElement.nativeElement.contains(e.target)
                && !this.dpPanelWrapperElement.nativeElement.contains(e.target)
                && !e.target?.classList?.contains('calendar-dropdown-item')
                && !e.target?.classList?.contains('calendar-dropdown-backdrop')) {
                this.showCalendar = false;
            }
        });
        
        if (this.datePickerClearDataSubject) {
            this.datePickerClearDataSubject.subscribe(() => {
                this.clearDatePickerData();
            });
        }
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.datePickerClearDataSubject = null;
    }

    /**
     * To formatted Date as string.
     *
     * @param _date Any.
     * @returns String.
     */
    toFormattedDate(_date: any): string {
        let response: string = this.PLACEHOLDER;
        
        if (_.isNil(_date)) {
            return response;
        }
        
        if (this.dateCache[_date.getTime()]) {
            return this.dateCache[_date.getTime()];
        }

        const defaultDateFormat: Required<FcDatePickerFormat> = {
            display: 'day/month/year',
            options: {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            }
        };
        const dateFormat: Required<FcDatePickerFormat> = {
            ...defaultDateFormat,
            ...this.dateFormat
        };
        let date = _date as Date;
        const year = new Intl.DateTimeFormat('en', {year: dateFormat.options.year}).format(date);
        const month = new Intl.DateTimeFormat('en', {month: dateFormat.options.month}).format(date);
        const day = new Intl.DateTimeFormat('en', {day: dateFormat.options.day}).format(date);
        if (!this.dateCache[_date.getTime()]) {
            response = this.dateCache[_date.getTime()] = (
                dateFormat.display
                    .replace('day', day)
                    .replace('month', month)
                    .replace('year', year)
            );
        }
        return response;
    }

    /**
     * To build input value label.
     *
     * @returns String.
     */
    getInputValueLabel(): string {
        let tmp = '';
        switch (this.datepickerType) {
        case this.DP_TYPES.SINGLE_DP:
        case this.DP_TYPES.SINGLE_DP_WITH_FILTER:
            if (!_.isNil(this.datepickerData.startDate)) {
                tmp = this.toFormattedDate(this.datepickerData.startDate);
            } else {
                tmp = this.inputPlaceholder || this.PLACEHOLDER;
            }
            break;
        case this.DP_TYPES.RANGE_DP:
        case this.DP_TYPES.RANGE_DP_WITH_FILTER:
            tmp = this.toFormattedDate(this.datepickerData.startDate) + ' - '
                + this.toFormattedDate(this.datepickerData.endDate);
            break;
        }

        return tmp;
    }


    /**
     * On filter change event.
     *
     * @param event Any.
     */
    onFilterChange(event: any) {
        this.filterChange.emit(event);
        let tmpData = _.first(event);
        if (!_.isNil(tmpData)) {
            _.unset(tmpData, ['_selected']);
        }
        this.datepickerData.filter = tmpData;

        this.emittingData();
    }

    /**
     * On single calendar change event.
     *
     * @param event Any.
     */
    onCalendarDateChange(event: any) {
        this.singleDatepickerModel = event;
        this.datepickerData.startDate = event;
        this.datepickerData.endDate = event;
        this.emittingData();
        if (this.closePanelAfterSelection) {
            if (this.datepickerType === this.DP_TYPES.SINGLE_DP
                || this.datepickerType === this.DP_TYPES.SINGLE_DP_WITH_FILTER) {
                this.showCalendar = false;
            } else {
                if (this.datepickerData.startDate && this.datepickerData.endDate) {
                    this.showCalendar = false;
                }
            }
        }
    }

    /**
     * In range mode, handle calendar from event.
     *
     * @param event Any.
     */
    onCalendarFromChange(event: any): void {
        let tmpDate = event;
        if (!_.isNil(this.datepickerData.endDate)) {
            if (event > this.datepickerData.endDate) {
                tmpDate = this.datepickerData.endDate;
            }
        }

        this.rangeFromDatepickerModel = tmpDate;
        this.datepickerData.startDate = tmpDate;
        this.emittingData();
    }

    /**
     * In range mode, handle calendar to event.
     *
     * @param event Any.
     */
    onCalendarToChange(event: any): void {
        let tmpDate = event;
        if (!_.isNil(this.datepickerData.startDate)) {
            if (event < this.datepickerData.startDate) {
                tmpDate = this.rangeEndDateGenerator( this.datepickerData.startDate.getFullYear(),
                    this.datepickerData.startDate.getMonth(),  this.datepickerData.startDate.getDate());
            }
        }

        this.rangeToDatepickerModel = tmpDate;
        this.datepickerData.endDate = tmpDate;
        this.emittingData();
    }

    /**
     * To determine filter enable or not.
     *
     * @returns Boolean.
     */
    hasFilterEnabled(): boolean {
        return this.datepickerType === this.DP_TYPES.SINGLE_DP_WITH_FILTER
            || this.datepickerType === this.DP_TYPES.RANGE_DP_WITH_FILTER;
    }

    /**
     * To determine single mode or dual calendar mode.
     *
     * @returns Boolean.
     */
    isSingleCalendar(): boolean {
        return this.datepickerType === this.DP_TYPES.SINGLE_DP_WITH_FILTER
            || this.datepickerType === this.DP_TYPES.SINGLE_DP;
    }

    private emittingData(): void {
        this.datepickerModelChange.emit(this.datepickerData);
    }

    /**
     * To check datepickerData has data or not.
     *
     * @returns Boolean.
     */
    hasDatepickerData(): boolean {
        return !(this.datepickerData.startDate || this.datepickerData.endDate);
    }

    /**
     * Clear The Datepicker Data.
     */
    clearDatePickerData(): void {
        this._datepickerData = {
            filter: this.filterItems && this.filterItems[0],
            startDate: null,
            endDate: null
        };

        this.rangeFromDatepickerModel = null;
        this.rangeToDatepickerModel = null;
        this.singleDatepickerModel = null;
        this.emittingData();
    }

}
